import { costHasInsufficientQuota, insufficientQuotaMessage } from './cost'
import { areaToString } from '../../units'
import intersect from '@turf/intersect'
import { intersects } from 'ol/extent'
import bbox from '@turf/bbox'
import { logMessage } from '../../logger'

export function getValidationMessage(props, state) {
  const validationState = new ValidationState(state, props)

  for (const [prop, messageFn] of validators) {
    if (validationState.get(prop)) {
      return messageFn(validationState)
    }
  }

  return null
}

const validators = [
  ['failedToLoadAny', () => `Images could not be loaded`],
  ['missingMetadata', () => 'Wait for images to load...'],
  [
    'isMixedFileType',
    () => 'Please upload JPEG and TIFF images in separate uploads',
  ],
  ['cameraPropertiesError', vs => vs.get('cameraPropertiesError')],
  [
    'multispectralWithoutPermission',
    () => (
      <>
        Multispectral 6+ band imagery is a feature of the Professional plan.
        Please{' '}
        <a href="/subscriptions" target="_blank">
          upgrade your plan
        </a>{' '}
        to upload this imagery.
      </>
    ),
  ],
  [
    'thermalWithoutPermission',
    () => (
      <>
        Thermal imagery is a feature of the Professional plan. Please{' '}
        <a href="/subscriptions" target="_blank">
          upgrade your plan
        </a>{' '}
        to upload this imagery.
      </>
    ),
  ],
  [
    'unmappedBands',
    () => 'Please choose band configuration for the Orthomosaic',
  ],
  [
    'isMixedDimensions',
    vs => {
      const dimensions = new Set()
      for (const feature of vs.state.metaData.features) {
        dimensions.add(
          `${feature.properties.width}x${feature.properties.height}`
        )
      }
      const firstDimensions = [...dimensions]
        .slice(0, 2)
        .map(d => `${d} px`)
        .join(' and ')

      return `Selected images are of different sizes: ${firstDimensions}. Please select images of one size only.`
    },
  ],
  [
    'missingFiles',
    vs =>
      vs.state.projectType !== 'geotiff'
        ? 'To resume the upload, click the "Add Missing Images" button in the top-left corner and select all images again. The Upload tool will automatically upload only the images not already uploaded.'
        : 'To resume the upload, click the "Add Missing File" button to the right and select all images again.',
  ],
  [
    'isMissingImagesInCaptures',
    () =>
      'Some captures are missing images. Please, remove them or add the missing images',
  ],
  [
    'tooFewCaptures',
    vs =>
      vs.get('minCaptures') > 1
        ? `Add at least ${vs.get('minCaptures')} ${
            vs.get('imagesPerCapture') > 1 ? 'captures' : 'images'
          } to upload`
        : 'Please select a GeoTIFF file to upload',
  ],
  [
    'hasInsufficientQuota',
    vs => (
      <>
        {insufficientQuotaMessage(vs.state.cost)} Please purchase{' '}
        <button
          className="tw-custom-button-reset tw-text-primary hover:tw-underline"
          onClick={() => vs.props.callbacks.onBuyCredits()}>
          additional credit
        </button>{' '}
        or{' '}
        <a className="tw-text-primary hover:tw-underline" href="/subscriptions">
          upgrade subscription
        </a>{' '}
        to process more data.
      </>
    ),
  ],
  [
    'tooManyFiles',
    vs => `Up to ${vs.get('maxFiles')} images allowed per upload`,
  ],
  [
    'tooLargeArea',
    vs =>
      `Area covered by the images is larger than ${areaToString(
        maxArea,
        vs.units,
        {}
      )}`,
  ],
  [
    'missingGeoreference',
    vs =>
      `${vs.get('numberMissingGeoreference')} ${
        vs.get('numberMissingGeoreference') !== 1 ? 'images are' : 'image is'
      } missing georefence. Remove ${
        vs.get('numberMissingGeoreference') !== 1 ? 'them' : 'it'
      } or use the Import Image Positions tool.`,
  ],
  [
    'invalidBounds',
    vs =>
      'The area covered by this image is invalid; check that CRS info is correct.',
  ],
  [
    'forbiddenArea',
    () =>
      "This imagery can't be processed. Please contact our support for more information.",
  ],
  ['missingFieldName', () => 'Please enter a field name before uploading'],
  [
    'costCalculationFailed',
    () => 'Could not calculate the cost of this upload',
  ],
  [
    'imagesOutsideField',
    vs =>
      `The selected images are located outside of the boundaries of the field "${vs.state.fieldName}". Please, choose another field that matches the location of the images or create a new one.`,
  ],
]

const maxArea = 1000 * 10000 // 1000 ha
const maxLength = 20000 // 20000 meters

class ValidationState {
  constructor(state, props) {
    this.state = state
    this.props = props
    this.properties = {}
  }

  get(prop) {
    if (!this.properties[prop]) {
      this.properties[prop] = this[prop]()
    }

    return this.properties[prop]
  }

  failedToLoadAny() {
    return !this.state.metaData && this.state.hasMetaDataErrors
  }

  missingMetadata() {
    return (
      !this.state.metaData ||
      this.state.metaData.features.length !== this.get('numberOfFiles')
    )
  }

  numberOfFiles() {
    return this.state.files && Object.keys(this.state.files).length
  }

  isMixedFileType() {
    let lastExt
    for (const id in this.state.files) {
      const parts = id.split('.')
      const ext = parts[parts.length - 1].toLowerCase()
      if (lastExt && lastExt !== ext) {
        return true
      }
      lastExt = ext
    }

    return false
  }

  isMixedDimensions() {
    if (this.state.projectType !== 'overlapping') {
      // Mixed dimensions are ok for scouting; geotiff
      // only has one image, so by definition it's not mixed
      return false
    }

    const captures = Object.values(this.state.captures || {})
    if (captures.length === 0) return false

    const fileIdToFeature = this.state.metaData.features.reduce(
      (acc, feature) => {
        acc[feature.id] = feature
        return acc
      },
      {}
    )

    let dimensions = getCaptureDimensions(captures[0])

    return captures.some(fileIds => {
      const captureDimensions = getCaptureDimensions(fileIds)
      if (dimensions.length < captureDimensions.length) {
        // The reference capture is incomplete (and will be
        // caught by another validator),
        // use this one as reference instead
        dimensions = captureDimensions
        return false
      }

      for (let i = 0; i < captureDimensions.length; i++) {
        const [width, height] = captureDimensions[i]
        if (width !== dimensions[i][0] || height !== dimensions[i][1]) {
          return true
        }
      }
    })

    // Returns an array of [width, height] pairs, sorted by width and then height
    // (lexicographically). The reason we sort them is that we currently don't
    // have captures properly sorted by band (they can be in any order), so we
    // need to make sure that we compare the same bands across captures.
    function getCaptureDimensions(fileIds) {
      return fileIds
        .map(fileId => {
          const { width, height } = fileIdToFeature[fileId].properties
          return [width, height]
        })
        .sort(compareWidthHeight)
    }
    function compareWidthHeight(a, b) {
      return a[0] - b[0] || a[1] - b[1]
    }
  }

  cameraPropertiesError() {
    return this.state.cameraProperties && this.state.cameraProperties.error
  }

  multispectralWithoutPermission() {
    return (
      this.state.cameraProperties?.bands?.length >= 6 &&
      !this.props.permissions.multispectral_upload
    )
  }

  thermalWithoutPermission() {
    return (
      this.state.cameraProperties?.bands?.includes('t') &&
      !this.props.permissions.multispectral_upload
    )
  }

  tooFewBands() {
    // For overlapping and scouting, we allow fewer than three bands, but for
    // GeoTIFF, we require at least three bands. (Fewer bands usually means it's
    // data we can't handle in a meaningful way; for overlapping, fewer bands can
    // only be uploaded for special cameras where we *do* know how to visualize.)
    return (
      this.state.projectType === 'geotiff' &&
      this.state.metaData?.features?.[0]?.properties.samplesPerPixel < 3
    )
  }

  unmappedBands() {
    const properties = this.state.metaData?.features?.[0]?.properties
    const numberBands = properties?.samplesPerPixel
    const bands = properties?.bands

    return (
      this.state.projectType === 'geotiff' &&
      (bands?.length !== numberBands || bands?.includes(' '))
    )
  }

  missingRedGreenBands() {
    return ['r', 'g'].some(
      reuqiredBand =>
        this.state.cameraProperties?.bands.indexOf(reuqiredBand) < 0
    )
  }

  isMissingImagesInCaptures() {
    return (
      this.state.captures &&
      Object.values(this.state.captures).some(
        files => files.length !== this.get('imagesPerCapture')
      )
    )
  }

  imagesPerCapture() {
    return (
      (this.state.cameraProperties &&
        this.state.cameraProperties.imagesPerCapture) ||
      1
    )
  }

  numberOfCaptures() {
    return this.state.captures && Object.keys(this.state.captures).length
  }

  minCaptures() {
    return this.state.projectType === 'geotiff' ? 1 : 3
  }

  tooFewCaptures() {
    return this.get('numberOfCaptures') < this.get('minCaptures')
  }

  hasInsufficientQuota() {
    return this.state.cost && costHasInsufficientQuota(this.state.cost)
  }

  costCalculationFailed() {
    return !this.state.cost
  }

  tooManyFiles() {
    return (
      this.get('hasMaxFiles') &&
      this.get('numberOfFiles') > this.get('maxFiles')
    )
  }

  hasMaxFiles() {
    return false
  }

  maxFiles() {
    return this.get('hasMaxFiles') ? 100 : null
  }

  tooLargeArea() {
    return (
      this.state.projectType === 'overlapping' &&
      this.state.projectMeasurements &&
      (this.state.projectMeasurements.area > maxArea ||
        this.state.projectMeasurements.length > maxLength)
    )
  }

  missingGeoreference() {
    return this.get('numberMissingGeoreference') > 0
  }

  numberMissingGeoreference() {
    return (
      this.state.metaData &&
      this.state.metaData.features.filter(
        f => !f.geometry && !f.properties.calibration_picture
      ).length
    )
  }

  missingFieldName() {
    return this.state.fieldName.trim().length === 0
  }

  missingFiles() {
    return this.state.isMissingFiles
  }

  invalidBounds() {
    return (
      this.state.projectType === 'geotiff' &&
      this.state.metaData?.features.some(
        feature => feature.bbox && !feature.bbox.every(c => Math.abs(c) <= 180)
      )
    )
  }

  forbiddenArea() {
    if (this.props.permissions.upload_all_areas) return false

    const bounds = this.state.projectMeasurements?.convexHull
      ? bbox(this.state.projectMeasurements?.convexHull)
      : null
    if (bounds) {
      const isForbidden = forbiddenAreas.some(area => intersects(area, bounds))
      if (isForbidden && !hasWarnedForbiddenArea) {
        logMessage(`Prevented user from uploading images in bounds ${bounds}`, {
          forbiddenArea: true,
        })
        hasWarnedForbiddenArea = true
      }

      return isForbidden
    }

    return false
  }

  imagesOutsideField() {
    const { fieldName, farmId, projectMeasurements } = this.state
    const convexHull = projectMeasurements?.epsg3857Hull

    if (!convexHull || !fieldName) return false

    const field = this.props.fields.find(
      field => field.label === fieldName && field.farm_id === farmId
    )

    if (!field || !field.geometry) return false

    return !intersect(convexHull, field.geometry)
  }
}

let hasWarnedForbiddenArea = false

const forbiddenAreas = [
  // Ecuador, mostly western part
  [-82, -2.5, -77, 2],
]
